import { Theme } from "@mui/material/styles";

export const SUCCESS_SNACKBAR = { variant: "success", anchorOrigin: { horizontal: "center", vertical: "top" }} as const;
export const ERROR_SNACKBAR = { variant: 'error', anchorOrigin: { horizontal: 'center', vertical: 'top' } } as const;
export const INFO_SNACKBAR = { variant: "info", anchorOrigin: { horizontal: "center", vertical: "top" } } as const;

export const SETTLEMENT_STATUS_TO_COLOR: Record<string, "info" | "success" | "warning"> = {
  W: "info",
  C: "success",
  N: "warning",
};

export const SUPPORT_TICKET_ENDPOINT = "/support-ticket/";
export const CLIENTS_ENDPOINT = "/settlement/clients/";
export const USERS_ENDPOINT = "/users/users/";
export const EQUIPMENTS_ENDPOINT = "/settlement/equipments/";
export const EQUIPMENT_PRODUCERS_ENDPOINT = "/settlement/equipment-producers/";
export const EQUIPMENT_ATTRIBUTES_ENDPOINT = "/settlement/equipment-attributes/";
export const EQUIPMENT_MODELS_ENDPOINT = "/settlement/equipment-models/";
export const EQUIPMENT_COUNTERS_ENDPOINT = "/settlement/equipment-counters/";
export const EQUIPMENT_COUNTERS_BULK_ENDPOINT = "/settlement/equipment-counters/bulk/";
export const CONTRACTS_ENDPOINT = "/settlement/contracts/";
export const CONTRACTS_SAVE_WITH_TERMS_ENDPOINT = "/settlement/contracts/save-with-terms/";
export const EXPIRED_CONTRACT_ACTIONS_ENDPOINT = "/settlement/expired-contract-actions/";
export const CONTRACTS_AUTOMATION_ENDPOINT = "/settlement/contracts-automation/";
export const CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT = "/settlement/contracts-automation/bulk-update/";
export const CONTRACT_TERMS_ENDPOINT = "/settlement/contract-terms/";
export const CLIENT_CONTRACT_TERMS_ENDPOINT = "/settlement/client-contract-terms/";
export const NUMERATION_SCHEMA_ENDPOINT = "/settlement/numeration-schema/";
export const SETTLEMENTS_ENDPOINT = "/settlement/settlements/";
export const SETTLEMENTS_PENDING_ACTIONS_ENDPOINT = "/settlement/settlements/pending-actions/";
export const SETTLEMENTS_NEEDS_REMINDER_ENDPOINT = "/settlement/settlements/needs-reminder/";
export const SETTLEMENTS_SEND_REMINDERS_ENDPOINT = "/settlement/settlements/send-reminders/";
export const SETTLEMENTS_PROFITABILITY_ENDPOINT = "/settlement/settlements/profitability/";
export const SETTLEMENTS_PROFITABILITY_STATS_ENDPOINT =
  "/settlement/settlements/profitability-stats/";
export const SETTLEMENTS_PROFITABILITY_CHARTS_ENDPOINT =
  "/settlement/settlements/profitability-charts/";
export const SETTLEMENTS_PROFITABILITY_BREAKDOWN_ENDPOINT =
  "/settlement/settlements/profitability-breakdown/";
export const getSettlementUpdateClientEmailEndpoint = (settlementId: string) =>
  `/settlement/settlements/${settlementId}/update-client-email/`;
export const SETTLEMENT_POSITIONS_ENDPOINT = "/settlement/settlement-positions/";
export const SETTLEMENT_EXTRA_POSITIONS_ENDPOINT =
  "/settlement/settlement-extra-positions/";
export const ANALYTICS_ENDPOINT = "/analytics/";
export const JOBS_ENDPOINT = "/service/jobs/";
export const JOBS_STATUS_TRANSITION_ENDPOINT = "/service/jobs/status-transition/";
export const JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT = "/service/job-status-transition-actions/";
export const JOB_TASKS_ENDPOINT = "/service/job-tasks/";
export const JOB_SERVICES_ENDPOINT = "/service/job-services/";
export const JOB_ISSUES_ENDPOINT = "/service/job-issues/";
export const EQUIPMENT_GROUPS_ENDPOINT = "/service/equipment-groups/";
export const EXTERNAL_SERVICES_ENDPOINT = "/service/external-services/";
export const EXTERNAL_SERVICES_CITY_SEARCH_ENDPOINT = "/service/external-services/city-search/";

// How far around a town an external service works - mirrors
// EXTERNAL_SERVICE_AREA_*_RADIUS_KM in backend/service/consts.py.
export const EXTERNAL_SERVICE_AREA_MIN_RADIUS_KM = 1;
export const EXTERNAL_SERVICE_AREA_MAX_RADIUS_KM = 300;
export const EXTERNAL_SERVICE_AREA_DEFAULT_RADIUS_KM = 20;
export const TECHNICIANS_ENDPOINT = "/analytics/technicians/";
export const BREAKDOWN_JOB_ISSUES_ENDPOINT = "/analytics/breakdown/job-issues/";
export const BREAKDOWN_JOB_SERVICES_ENDPOINT = "/analytics/breakdown/job-services/";
export const BREAKDOWN_USED_PARTS_ENDPOINT = "/analytics/breakdown/used-parts/";
export const BREAKDOWN_TECHNICIANS_ENDPOINT = "/analytics/breakdown/technicians/";
export const BREAKDOWN_EQUIPMENT_ENDPOINT = "/analytics/breakdown/equipment/";
export const BREAKDOWN_EQUIPMENT_USED_PARTS_ENDPOINT = "/analytics/breakdown/equipment-used-parts/";
export const BREAKDOWN_JOBS_ENDPOINT = "/analytics/breakdown/jobs/";
export const GENERAL_SERVICE_STATS_ENDPOINT = "/analytics/general-service-stats/";
export const ATTACHMENTS_ENDPOINT = "/attachments/attachments/";
export const PORTAL_CONTACTS_ENDPOINT = "/portal/contacts/";
// Dispatched after a portal contact is created or edited, so the grid on the
// client's Portal access tab reloads - the form lives in the toolbar, outside
// the grid's own tree.
export const PORTAL_CONTACTS_REFRESH_EVENT = "clientPortalContactsChanged";
export const PORTAL_SETTINGS_ENDPOINT = "/portal/settings/";
export const PORTAL_JOBS_ENDPOINT = "/portal/jobs/";
export const PORTAL_ME_ENDPOINT = "/portal/me/";
export const PORTAL_EQUIPMENT_ENDPOINT = "/portal/equipment/";
export const PORTAL_COUNTERS_ENDPOINT = "/portal/counters/";
export const PORTAL_TICKETS_ENDPOINT = "/portal/tickets/";
export const getPortalTicketEndpoint = (jobId: number | string) =>
  `/portal/tickets/${jobId}/`;
export const PORTAL_ATTACHMENTS_ENDPOINT = "/portal/attachments/";
export const PORTAL_REQUEST_CODE_ENDPOINT = "/portal/auth/request-code/";
export const PORTAL_TOKEN_ENDPOINT = "/portal/auth/token/";
export const getPortalLookupEndpoint = (slug: string) => `/portal/lookup/${slug}/`;
export const MESSAGES_ENDPOINT = "/messaging/messages/";
export const MESSAGES_MARK_READ_ENDPOINT = "/messaging/messages/mark-read/";
export const MESSAGES_UNREAD_COUNTS_ENDPOINT = "/messaging/messages/unread-counts/";
export const NOTIFICATIONS_ENDPOINT = "/messaging/messages/notifications/";
export const NOTIFICATIONS_UNREAD_COUNT_ENDPOINT =
  "/messaging/messages/notifications-unread-count/";
export const NOTIFICATIONS_MARK_READ_ENDPOINT =
  "/messaging/messages/notifications-mark-read/";
export const PARTS_ENDPOINT = "/service/parts/";
export const ORDERED_PARTS_ENDPOINT = "/service/ordered-parts/";
export const buildOrderedPartMarkDeliveredEndpoint = (id: string | number) =>
  `/service/ordered-parts/${id}/mark-delivered/`;
export const USED_PARTS_ENDPOINT = "/service/used-parts/";
export const TENANT_SETTINGS_ENDPOINT = "/config/tenant-settings/";
export const INVOICES_ENDPOINT = "/invoice/invoices/";
export const INVOICES_SUMMARY_ENDPOINT = "/invoice/invoices/summary/";
export const INVOICES_LAST_ISSUED_ENDPOINT = "/invoice/invoices/last-issued/";
export const INVOICES_FROM_SETTLEMENT_ENDPOINT = "/invoice/invoices/from-settlement/";
export const INVOICES_FROM_JOB_ENDPOINT = "/invoice/invoices/from-job/";
export const INVOICES_BULK_SEND_ENDPOINT = "/invoice/invoices/bulk-send/";
export const INVOICES_BULK_SEND_TO_KSEF_ENDPOINT = "/invoice/invoices/bulk-send-to-ksef/";
// Mirrors BULK_ACTION_MAX_INVOICES in backend/invoice/consts.py.
export const INVOICE_BULK_ACTION_MAX = 200;
export const buildInvoiceIssueEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/issue/`;
export const buildInvoicePdfEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/pdf/`;
export const buildInvoiceRegeneratePdfEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/regenerate-pdf/`;
export const buildInvoiceSendEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/send/`;
export const buildInvoiceSendToKsefEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/send-to-ksef/`;
export const buildInvoiceMarkPaidEndpoint = (id: string | number) =>
  `/invoice/invoices/${id}/mark-paid/`;
export const CLIENT_INVOICE_SETTINGS_ENDPOINT = "/invoice/client-invoice-settings/";
export const buildClientInvoiceSettingsEndpoint = (clientId: string | number) =>
  `/invoice/client-invoice-settings/${clientId}/`;
export const buildClientLastInvoiceDefaultsEndpoint = (clientId: string | number) =>
  `/invoice/client-invoice-settings/${clientId}/last-invoice/`;
export const CLIENT_INVOICE_SETTINGS_UPDATED_EVENT = "clientInvoiceSettingsUpdated";
export const PURCHASE_INVOICES_ENDPOINT = "/invoice/purchase-invoices/";
export const PURCHASE_INVOICES_DOWNLOAD_FROM_KSEF_ENDPOINT =
  "/invoice/purchase-invoices/download-from-ksef/";
export const PURCHASE_INVOICES_DOWNLOAD_STATUS_ENDPOINT =
  "/invoice/purchase-invoices/download-status/";
export const buildPurchaseInvoiceXmlEndpoint = (id: string | number) =>
  `/invoice/purchase-invoices/${id}/xml/`;
export const EMAIL_SERVICE_STATUS_ENDPOINT = "/config/email-service-status/";
export const MAIL_INGESTION_LOGS_ENDPOINT = "/settlement/mail-ingestion-logs/";
export const MAIL_INGESTION_RUN_ENDPOINT = "/settlement/mail-ingestion-logs/run/";
export const ONLINE_USERS_ENDPOINT = "/users/online-users/";
export const MY_LOCATION_ENDPOINT = "/users/my-location/";
export const BIOMETRIC_SETUP_OFFERED_ENDPOINT = "/users/current-user/biometric-setup-offered/";
export const TECHNICIAN_LOCATIONS_ENDPOINT = "/users/technician-locations/";
export const buildTechnicianLocationAddressEndpoint = (userId: number) =>
  `/users/technician-locations/${userId}/address/`;
export const SERVICE_REMINDERS_ENDPOINT = "/reminders/service-reminders/";
export const SERVICE_REMINDERS_SUMMARY_ENDPOINT = "/reminders/service-reminders/summary/";
export const buildServiceReminderSendEndpoint = (id: string | number) =>
  `/reminders/service-reminders/${id}/send/`;
export const buildServiceReminderCompleteEndpoint = (id: string | number) =>
  `/reminders/service-reminders/${id}/complete/`;

export const buildInstanceHistoryEndpoint = (
  modelName: string,
  objectId: string | number
) => `${ANALYTICS_ENDPOINT}history/${modelName}/${objectId}/`;

export const buildEquipmentServiceStatsEndpoint = (
  equipmentId: string | number
) => `${ANALYTICS_ENDPOINT}equipment/${equipmentId}/service-stats/`;

export const buildEquipmentFullServiceStatsEndpoint = (
  equipmentId: string | number
) => `${ANALYTICS_ENDPOINT}equipment/${equipmentId}/full-service-stats/`;

export const buildEquipmentGroupServiceStatsEndpoint = (
  groupId: string | number
) => `${ANALYTICS_ENDPOINT}equipment-group/${groupId}/service-stats/`;

export const EQUIPMENT_COUNTERS_SOURCE_EQUIPMENT_ENDPOINT = `${ANALYTICS_ENDPOINT}equipment-counters/equipment/`;

export const buildTechnicianStatsEndpoint = (
  technicianId: string | number
) => `${TECHNICIANS_ENDPOINT}${technicianId}/stats/`;

export const JOB_STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "P", labelKey: "Pending" },
  { value: "IP", labelKey: "In Progress" },
  { value: "FV", labelKey: "For Verification" },
  { value: "FI", labelKey: "For Improvement" },
  { value: "F", labelKey: "Finished" },
];

export const INVOICE_STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "D", labelKey: "Draft" },
  { value: "I", labelKey: "Issued" },
  { value: "C", labelKey: "Cancelled" },
];

export const INVOICE_STATUS_COLOR_MAP: Record<string, "default" | "success" | "error"> = {
  D: "default",
  I: "success",
  C: "error",
};

export const INVOICE_KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "VAT", labelKey: "VAT invoice" },
  { value: "KOR", labelKey: "Correction invoice" },
  { value: "ZAL", labelKey: "Advance invoice" },
  { value: "ROZ", labelKey: "Settlement invoice" },
  { value: "UPR", labelKey: "Simplified invoice" },
  { value: "PRO", labelKey: "Proforma" },
];

// FA(3) Podmiot3/Rola codes an invoice receiver (odbiorca) can take. A
// subordinate local government unit and a VAT group member also need the
// receiver to have a NIP - KSeF shares the invoice with them by it.
export const RECEIVER_ROLE = {
  RECEIVER: "2",
  LOCAL_GOVERNMENT_UNIT: "8",
  VAT_GROUP_MEMBER: "10",
} as const;

export const RECEIVER_ROLE_OPTIONS: { value: string; labelKey: string }[] = [
  {
    value: RECEIVER_ROLE.LOCAL_GOVERNMENT_UNIT,
    labelKey: "Subordinate local government unit (e.g. school, city office)",
  },
  { value: RECEIVER_ROLE.RECEIVER, labelKey: "Receiver (branch, internal unit)" },
  { value: RECEIVER_ROLE.VAT_GROUP_MEMBER, labelKey: "VAT group member" },
];

// FA(3) Platnosc/FormaPlatnosci codes - the value is what the XML carries.
export const INVOICE_PAYMENT_METHOD_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "6", labelKey: "Bank transfer" },
  { value: "1", labelKey: "Cash" },
  { value: "2", labelKey: "Card" },
  { value: "3", labelKey: "Voucher" },
  { value: "4", labelKey: "Cheque" },
  { value: "5", labelKey: "Credit" },
  { value: "7", labelKey: "Mobile payment" },
];

// FA(3) P_12. Not numbers: the schema also allows procedure codes that carry
// no percentage at all (exempt, reverse charge, out of scope).
export const VAT_RATE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "23", labelKey: "23%" },
  { value: "8", labelKey: "8%" },
  { value: "5", labelKey: "5%" },
  { value: "0 KR", labelKey: "0% domestic" },
  { value: "0 WDT", labelKey: "0% intra-community supply" },
  { value: "0 EX", labelKey: "0% export" },
  { value: "zw", labelKey: "Exempt" },
  { value: "oo", labelKey: "Reverse charge" },
  { value: "np I", labelKey: "Not subject to tax (I)" },
  { value: "np II", labelKey: "Not subject to tax (II)" },
];

/** How settlement automation gathers confirmed settlements into invoices. */
export const INVOICE_GROUPING_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "S", labelKey: "One invoice per settlement" },
  { value: "C", labelKey: "One invoice per contract" },
  { value: "L", labelKey: "One invoice per client" },
];

export const KSEF_STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "N", labelKey: "Not sent" },
  { value: "P", labelKey: "Submission pending" },
  { value: "A", labelKey: "Accepted" },
  { value: "R", labelKey: "Rejected" },
  { value: "X", labelKey: "Excluded" },
];

export const KSEF_STATUS_COLOR_MAP: Record<string, "default" | "info" | "success" | "error"> = {
  N: "default",
  P: "info",
  A: "success",
  R: "error",
  X: "default",
};

/** TenantSettings.ksef_auth_method - how the tenant authenticates to KSeF. */
export const KSEF_AUTH_METHOD = {
  TOKEN: "T",
  CERTIFICATE: "C",
} as const;

const toLabelKeyMap = (options: { value: string; labelKey: string }[]) =>
  options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.labelKey;
    return acc;
  }, {});

// Status labels are rendered from these rather than from the API's
// *_display fields: those come from Django's own catalogue, which is not the
// same one the UI is translated with.
export const INVOICE_STATUS_LABEL_KEYS = toLabelKeyMap(INVOICE_STATUS_OPTIONS);
export const KSEF_STATUS_LABEL_KEYS = toLabelKeyMap(KSEF_STATUS_OPTIONS);
export const INVOICE_KIND_LABEL_KEYS = toLabelKeyMap(INVOICE_KIND_OPTIONS);
export const VAT_RATE_LABEL_KEYS = toLabelKeyMap(VAT_RATE_OPTIONS);
export const INVOICE_PAYMENT_METHOD_LABEL_KEYS = toLabelKeyMap(
  INVOICE_PAYMENT_METHOD_OPTIONS
);
export const RECEIVER_ROLE_LABEL_KEYS = toLabelKeyMap(RECEIVER_ROLE_OPTIONS);
export const INVOICE_GROUPING_LABEL_KEYS = toLabelKeyMap(INVOICE_GROUPING_OPTIONS);

// KSeF `InvoiceType` values, as its metadata reports them for purchase
// invoices. A different vocabulary from INVOICE_KIND_OPTIONS: it also covers
// the PEF and RR document families, which Copigo never issues itself.
export const KSEF_INVOICE_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "Vat", labelKey: "VAT invoice" },
  { value: "Zal", labelKey: "Advance invoice" },
  { value: "Kor", labelKey: "Correction invoice" },
  { value: "Roz", labelKey: "Settlement invoice" },
  { value: "Upr", labelKey: "Simplified invoice" },
  { value: "KorZal", labelKey: "Advance correction invoice" },
  { value: "KorRoz", labelKey: "Settlement correction invoice" },
  { value: "VatPef", labelKey: "PEF invoice" },
  { value: "VatPefSp", labelKey: "Specialised PEF invoice" },
  { value: "KorPef", labelKey: "PEF correction invoice" },
  { value: "VatRr", labelKey: "VAT RR invoice" },
  { value: "KorVatRr", labelKey: "VAT RR correction invoice" },
];
export const KSEF_INVOICE_TYPE_LABEL_KEYS = toLabelKeyMap(
  KSEF_INVOICE_TYPE_OPTIONS
);

export const JOB_STATUS_COLOR_MAP: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "warning" | "success"> = {
  P: "warning",
  IP: "primary",
  FV: "info",
  FI: "error",
  F: "success",
};

// Mirrors backend service.consts.PartStatus - the state of an ordered batch.
export const PART_STATUS_COLOR_MAP: Record<string, "default" | "info" | "success"> = {
  O: "info",
  D: "success",
  U: "default",
};

export const JOB_KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "I", labelKey: "Internal" },
  { value: "EWIC", labelKey: "External with contract" },
  { value: "EWOC", labelKey: "External without contract" },
  { value: "C", labelKey: "Complaint" },
];

export const JOB_KIND_COLOR_MAP: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "warning" | "success"> = {
  I: "default",
  EWIC: "success",
  EWOC: "warning",
  C: "error",
};

// Mirrors backend settlement.consts.EquipmentKind. Black-only equipment cannot
// produce color pages - the backend rejects a "CP" counter on it (see
// settlement/services/equipment.py::validate_kind) - so anything color-specific
// (counter kinds, per-page usage costs) is hidden for this kind.
export const BLACK_ONLY_EQUIPMENT_KIND = "B";

// Mirrors backend settlement.consts.EquipmentKind.
export const EQUIPMENT_KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "B", labelKey: "Black and white" },
  { value: "C", labelKey: "Color" },
];

// Mirrors backend settlement.consts.Ownership.
export const EQUIPMENT_OWNERSHIP_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "O", labelKey: "Own" },
  { value: "L", labelKey: "Leasing" },
  { value: "C", labelKey: "Client's" },
];

// Mirrors backend service.consts.PartStatus.
/** Placed, not delivered yet - nothing of it is on the shelf. */
export const PART_STATUS_ORDERED = "O";
export const PART_STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "O", labelKey: "Ordered" },
  { value: "D", labelKey: "Delivered" },
  { value: "U", labelKey: "Used" },
];

// The three roll-up layers of the settlements Profitability tab. Mirrors
// backend settlement.selectors.PROFITABILITY_GROUP_BY.
export const PROFITABILITY_GROUP_BY_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "equipment", labelKey: "Per equipment" },
  { value: "contract", labelKey: "Per contract" },
  { value: "client", labelKey: "Per client" },
];

// Mirrors backend settlement.consts.SettlementKind (ContractTerm.settlement_kind).
export const SETTLEMENT_KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "U", labelKey: "Per usage" },
  { value: "RR", labelKey: "Rent without limits" },
  { value: "RL", labelKey: "Rent with usage limit" },
  { value: "RA", labelKey: "Rent with amount limit" },
];

// Mirrors backend settlement.consts.SettlementCycle (ContractTerm.settlement_cycle).
export const SETTLEMENT_CYCLE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "D", labelKey: "Daily" },
  { value: "M", labelKey: "Monthly" },
  { value: "Q", labelKey: "Quaterly" },
];

// Mirrors backend settlement.consts.ContractTermKind (ContractTerm.kind).
export const CONTRACT_TERM_KIND_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "CCG", labelKey: "Separate counter" },
  { value: "CCR", labelKey: "Common counter" },
];

// Explicit badge colors for Job.kind chips (Job list and Job details).
// COMPLAINT ("C") is intentionally excluded - it keeps using JOB_KIND_COLOR_MAP's
// theme "error" color instead of a custom pair.
// EXTERNAL_WITH_CONTRACT ("EWIC") uses a transparent-fill, border-only style
// per client request, instead of the solid pastel fill the other kinds get.
// Each kind has a light-theme pair (solid pastel bg + dark text) and a
// dark-theme pair (translucent tinted bg + light text), since a single fixed
// hex pair does not read well against both a white and a near-black surface.
const JOB_KIND_BADGE_STYLE_MAP: Record<
  string,
  {
    light: { bgcolor: string; color: string; border?: string };
    dark: { bgcolor: string; color: string; border?: string };
  }
> = {
  I: {
    light: { bgcolor: "#F2E7FF", color: "#6B21A8" },
    dark: { bgcolor: "rgba(168, 85, 247, 0.28)", color: "#E9D5FF" },
  },
  EWOC: {
    light: { bgcolor: "#CDFFD1", color: "#15803D" },
    dark: { bgcolor: "rgba(34, 197, 94, 0.28)", color: "#BBF7D0" },
  },
  EWIC: {
    light: { bgcolor: "transparent", color: "#1D4ED8", border: "1px solid #1D4ED8" },
    dark: { bgcolor: "transparent", color: "#93C5FD", border: "1px solid #60A5FA" },
  },
};

export const getJobKindChipSx = (kind: string) => {
  const style = JOB_KIND_BADGE_STYLE_MAP[kind];
  if (!style) return undefined;
  return (theme: Theme) => {
    const variant = theme.palette.mode === "dark" ? style.dark : style.light;
    return {
      bgcolor: variant.bgcolor,
      color: variant.color,
      ...(variant.border ? { border: variant.border } : {}),
      "& .MuiChip-label": { color: variant.color },
    };
  };
};

const JOB_KIND_LABEL_KEY_BY_VALUE = JOB_KIND_OPTIONS.reduce<Record<string, string>>(
  (acc, option) => {
    acc[option.value] = option.labelKey;
    return acc;
  },
  {}
);

export const getJobKindLabelKey = (kind: string): string | undefined =>
  JOB_KIND_LABEL_KEY_BY_VALUE[kind];

export const JOB_NEXT_STATUSES_MAP: Record<string, string[]> = {
  P: ["IP"],
  IP: ["FV"],
  FV: ["FI", "F"],
  FI: ["FV"],
  F: [],
};

// Statuses in which a job assigned to an "own"-scoped user (e.g. Serwisant) can be
// edited/deleted, as opposed to only viewed - mirrors backend's OWN_JOB_EDITABLE_STATUSES.
export const OWN_JOB_EDITABLE_STATUSES = ["P", "IP", "FV", "FI"];

// Statuses from which an "own"-scoped user (e.g. Serwisant) may initiate a status
// change. Narrower than OWN_JOB_EDITABLE_STATUSES - Serwisant should not be able to
// move a job out of "For Verification" themselves.
export const OWN_JOB_STATUS_TRANSITION_STATUSES = ["P", "IP", "FI"];

const JOB_STATUS_LABEL_KEY_BY_VALUE = JOB_STATUS_OPTIONS.reduce<Record<string, string>>(
  (acc, option) => {
    acc[option.value] = option.labelKey;
    return acc;
  },
  {}
);

export const getJobStatusLabelKey = (status: string): string | undefined =>
  JOB_STATUS_LABEL_KEY_BY_VALUE[status];

// Query parameter the landing page uses to hand over the plan a visitor picked
// there, so the registration form opens with it already selected.
export const REGISTER_PLAN_QUERY_PARAM = "plan";
